import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/Store';
import { FeatureGroupNode, Feature, Role, RbacUser, PermissionTreeForest, PermissionTreeNode, AnnotatedFeature, FlattenedFeature, ModelRecordRestriction } from 'types/apis/rbacInterfaces';
import { UI_PAGES, UI_REGISTRY_GROUP_KEY, isUiEndpoint } from 'framework/access/UiRegistry';
import { BFF_FEATURES } from 'framework/access/BffFeatureCatalog';

export type RbacEntityType = 'role' | 'user';

export interface RbacReducerState {
  featureGroupsTree: FeatureGroupNode[];
  uncategorizedFeatures: Feature[];
  roles: Role[];
  users: RbacUser[];
  selectedEntityType?: RbacEntityType;
  selectedEntityId?: number | string;
  permissionTree?: PermissionTreeForest;
  modelRestrictions?: ModelRecordRestriction[];
  /** Model keys a restriction rule actually filters today — see proxypulse rbac/permissions.py:ENFORCED_MODELS. */
  enforcedModels?: string[];
  loading: boolean;
  error?: string;
}

const defaultState: RbacReducerState = {
  featureGroupsTree: [],
  uncategorizedFeatures: [],
  roles: [],
  users: [],
  selectedEntityType: undefined,
  selectedEntityId: undefined,
  permissionTree: undefined,
  modelRestrictions: undefined,
  enforcedModels: undefined,
  loading: false,
  error: undefined,
};

export const rbacSlice = createSlice({
  name: 'rbac',
  initialState: defaultState,
  reducers: {
    setFeatureGroupsTree: (state, action: PayloadAction<{ groups: FeatureGroupNode[]; uncategorized_features: Feature[] }>) => {
      state.featureGroupsTree = action.payload.groups;
      state.uncategorizedFeatures = action.payload.uncategorized_features;
    },
    setRoles: (state, action: PayloadAction<Role[]>) => {
      state.roles = action.payload;
    },
    setUsers: (state, action: PayloadAction<RbacUser[]>) => {
      state.users = action.payload;
    },
    setSelectedEntity: (state, action: PayloadAction<{ type: RbacEntityType; id: number | string }>) => {
      state.selectedEntityType = action.payload.type;
      state.selectedEntityId = action.payload.id;
      state.permissionTree = undefined;
      state.modelRestrictions = undefined;
    },
    setPermissionTree: (state, action: PayloadAction<PermissionTreeForest | undefined>) => {
      state.permissionTree = action.payload;
    },
    setModelRestrictions: (state, action: PayloadAction<ModelRecordRestriction[] | undefined>) => {
      state.modelRestrictions = action.payload;
    },
    setEnforcedModels: (state, action: PayloadAction<string[]>) => {
      state.enforcedModels = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    resetState: () => defaultState,
  },
});

export const {
  setFeatureGroupsTree,
  setRoles,
  setUsers,
  setSelectedEntity,
  setPermissionTree,
  setModelRestrictions,
  setEnforcedModels,
  setLoading,
  setError,
  resetState,
} = rbacSlice.actions;

export const selectFeatureGroupsTree = (state: RootState) => state.rbac.featureGroupsTree;
export const selectUncategorizedFeatures = (state: RootState) => state.rbac.uncategorizedFeatures;
export const selectRoles = (state: RootState) => state.rbac.roles;
export const selectUsers = (state: RootState) => state.rbac.users;
export const selectSelectedEntityType = (state: RootState) => state.rbac.selectedEntityType;
export const selectSelectedEntityId = (state: RootState) => state.rbac.selectedEntityId;
export const selectPermissionTree = (state: RootState) => state.rbac.permissionTree;
export const selectModelRestrictions = (state: RootState) => state.rbac.modelRestrictions;
export const selectEnforcedModels = (state: RootState): string[] => state.rbac.enforcedModels || [];
export const selectRbacLoading = (state: RootState) => state.rbac.loading;
export const selectRbacError = (state: RootState) => state.rbac.error;

const stripUiFeatures = (node: PermissionTreeNode): PermissionTreeNode | null => {
  if (node.key === UI_REGISTRY_GROUP_KEY) return null;
  const children = node.children.map(stripUiFeatures).filter((c): c is PermissionTreeNode => c !== null);
  const features = node.features.filter((f) => !isUiEndpoint(f.endpoint));
  return { ...node, children, features };
};

/**
 * The selected entity's permission tree with every ui.* row removed — feeds
 * the "API Permissions" category tab. The ui_registry group and any
 * uncategorized ui.* feature never appear here (design doc §1.4/§7.5): a
 * ui.* feature is identified by its endpoint prefix, not by group membership,
 * since a feature belongs to exactly one group and could be moved into a
 * custom access group.
 */
export const selectApiPermissionForest = (state: RootState): PermissionTreeForest | undefined => {
  const tree = state.rbac.permissionTree;
  if (!tree) return undefined;
  return {
    ...tree,
    groups: tree.groups.map(stripUiFeatures).filter((g): g is PermissionTreeNode => g !== null),
    uncategorized_features: tree.uncategorized_features.filter((f) => !isUiEndpoint(f.endpoint)),
  };
};

export interface UiPermissionSlice {
  /** ui.* rows only — grant state for registry pages/CTAs. */
  byEndpoint: Map<string, FlattenedFeature>;
  /**
   * EVERY feature in the forest keyed by endpoint (ui.* AND API features) —
   * lets the Pages & Actions panel resolve/grant the API actions nested under
   * each registry entry without a second fetch.
   */
  allByEndpoint: Map<string, FlattenedFeature>;
  orphans: FlattenedFeature[];
}

const GRANT_STATE_RANK: Record<AnnotatedFeature['grant_state'], number> = { granted: 2, partial: 1, none: 0 };

/**
 * Merges one occurrence of a feature into the running map, keyed by endpoint.
 * A feature filed into multiple groups appears once per owning group node in
 * the tree walk below — merge (not overwrite) so no branch's grant_state or
 * granted_via_group_id is silently dropped in favor of whichever was visited last.
 */
const mergeFeature = (map: Map<string, FlattenedFeature>, f: AnnotatedFeature) => {
  const prior = map.get(f.endpoint);
  const viaIds = f.granted_via_group_id != null ? [f.granted_via_group_id] : [];
  if (!prior) {
    map.set(f.endpoint, { ...f, granted_via_group_ids: viaIds });
    return;
  }
  const grant_state = GRANT_STATE_RANK[f.grant_state] > GRANT_STATE_RANK[prior.grant_state] ? f.grant_state : prior.grant_state;
  const newIds = viaIds.filter((id) => !prior.granted_via_group_ids.includes(id));
  map.set(f.endpoint, {
    ...prior,
    grant_state,
    granted_directly: prior.granted_directly || f.granted_directly,
    granted_via_group_ids: newIds.length ? [...prior.granted_via_group_ids, ...newIds] : prior.granted_via_group_ids,
  });
};

/**
 * The selected entity's grants resolved by endpoint across the WHOLE forest
 * (any group, or uncategorized) — feeds the "Pages & Actions" category tab.
 * `orphans` are BE ui.* rows no longer present in UI_PAGES (renamed or
 * removed registry entries) — surfaced for revoke, never auto-deleted.
 */
export const selectUiPermissionSlice = (state: RootState): UiPermissionSlice => {
  const tree = state.rbac.permissionTree;
  const byEndpoint = new Map<string, FlattenedFeature>();
  const allByEndpoint = new Map<string, FlattenedFeature>();
  if (!tree) return { byEndpoint, allByEndpoint, orphans: [] };

  const collectFeature = (f: AnnotatedFeature) => {
    mergeFeature(allByEndpoint, f);
    if (isUiEndpoint(f.endpoint)) mergeFeature(byEndpoint, f);
  };
  const collect = (node: PermissionTreeNode) => {
    node.features.forEach(collectFeature);
    node.children.forEach(collect);
  };
  tree.groups.forEach(collect);
  tree.uncategorized_features.forEach(collectFeature);

  const knownIds = new Set(
    Object.values(UI_PAGES).flatMap((p) => [p.id as string, ...Object.values(p.ctas).map((c) => c.id as string)]),
  );
  const orphans = Array.from(byEndpoint.values()).filter((f) => !knownIds.has(f.endpoint));

  return { byEndpoint, allByEndpoint, orphans };
};

export interface ModelSummary {
  /** BFF_FEATURES domain key, e.g. 'ENVIRONMENTS' — doubles as the model id. */
  key: string;
  label: string;
  grantedActions: number;
  totalActions: number;
  /** True if the entity holds the specific list action a restriction rule would actually
   * filter (see MODEL_LIST_ACTIONS) — the hierarchy a restriction can only narrow, never
   * substitute for, API access. False means adding a rule here would have no effect: the
   * list endpoint itself is still closed off regardless of any restriction. */
  listActionGranted: boolean;
}

const humanizeModelKey = (key: string) =>
  key.toLowerCase().split('_').map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');

/**
 * The list-endpoint action(s) each ENFORCED_MODELS key is actually filtered on server-side —
 * mirrors proxypulse rbac/permissions.py:ENFORCED_MODELS' own comment (environments_list_all,
 * utilities_list, projects_list/unarchived_projects_list). A restriction rule is only ever
 * consulted from within these actions, so granting the rule without granting the action it
 * scopes is a no-op, not a narrower grant.
 */
const MODEL_LIST_ACTIONS: Record<string, string[]> = {
  ENVIRONMENTS: [BFF_FEATURES.ENVIRONMENTS.LIST_ALL],
  UTILITY_BFF: [BFF_FEATURES.UTILITY_BFF.LIST],
  PROJECTS: [BFF_FEATURES.PROJECTS.LIST, BFF_FEATURES.PROJECTS.UNARCHIVED_LIST],
};

/**
 * ENFORCED_MODELS the selected entity holds at least one granted action in — "models who have
 * role or user association" for the Model Permissions tab. Models outside ENFORCED_MODELS are
 * left out entirely rather than shown disabled: a restriction there would never be consulted by
 * any list endpoint, so surfacing it just invites an admin to configure something inert.
 */
export const selectModelsWithAssociation = (state: RootState): ModelSummary[] => {
  const { allByEndpoint } = selectUiPermissionSlice(state);
  const enforcedModels = state.rbac.enforcedModels || [];
  const summaries: ModelSummary[] = [];

  const isGranted = (action: string) => {
    const grantState = allByEndpoint.get(action)?.grant_state;
    return grantState === 'granted' || grantState === 'partial';
  };

  Object.entries(BFF_FEATURES).forEach(([key, domain]) => {
    if (!enforcedModels.includes(key)) return;
    const actions = Object.values(domain);
    const grantedActions = actions.filter(isGranted).length;
    if (grantedActions === 0) return;
    const listActionGranted = (MODEL_LIST_ACTIONS[key] || []).some(isGranted);
    summaries.push({ key, label: humanizeModelKey(key), grantedActions, totalActions: actions.length, listActionGranted });
  });

  return summaries;
};

/** Groups auto-discovered from a live backend blueprint — read-mostly in the admin UI. `is_system` is
 * set once by proxypulse at creation time (discovery.get_or_create_feature_group) and never flips. */
export const selectSystemGroups = (state: RootState): FeatureGroupNode[] =>
  state.rbac.featureGroupsTree.filter((g) => g.key !== UI_REGISTRY_GROUP_KEY && g.is_system);

/** Admin-created groups — full CRUD. Excludes the implicit ui_registry group (managed by the Pages panel). */
export const selectCustomGroups = (state: RootState): FeatureGroupNode[] =>
  state.rbac.featureGroupsTree.filter((g) => g.key !== UI_REGISTRY_GROUP_KEY && !g.is_system);

const collectFeatureGroupIds = (nodes: FeatureGroupNode[]): Set<number> => {
  const ids = new Set<number>();
  const walk = (list: FeatureGroupNode[]) => list.forEach((n) => {
    ids.add(n.id);
    walk(n.children || []);
  });
  walk(nodes);
  return ids;
};

/**
 * The selected entity's permission tree (grant_state per group already annotated server-side),
 * pruned down to just the custom-group roots and their nested subgroups — feeds the "Access
 * Groups" category tab so an admin can grant/revoke a hand-curated bundle directly instead of
 * hunting for its checkbox inside the full API Permissions tree. System (blueprint) groups are
 * excluded on purpose: they're auto-discovered, not bundles meant for ad-hoc assignment.
 */
export const selectCustomGroupsPermissionForest = (state: RootState): PermissionTreeNode[] | undefined => {
  const tree = state.rbac.permissionTree;
  if (!tree) return undefined;
  const customIds = collectFeatureGroupIds(selectCustomGroups(state));
  return tree.groups.filter((g) => customIds.has(g.id));
};

export default rbacSlice.reducer;
